import { Children, forwardRef, isValidElement, useMemo } from 'react';
import MuiButton from '@mui/material/Button';
import { Theme, useTheme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { Typography } from '../Typography/Typography';
import {
    DesignSystemButtonColorScheme,
    DesignSystemButtonRadius,
    DesignSystemButtonSize,
    DesignSystemButtonVariant,
} from '../../theme/design-system/types';
import {
    buttonRadiusValues,
    buttonSizeValues,
    getSizeStyles,
    getVariantColorStyles,
    loadingIndicatorHorizontalPositionValues,
} from '@upheal/design-system/components/Button/styleUtils';
import { focusWidthValues } from '@upheal/design-system/theme/design-system/tokens';
import { ButtonProps } from './Button.types';
import { SlotProvider } from '@upheal/design-system/utils/SlotContext';
import { Stack } from '@mui/material';
import { Slots } from '@upheal/design-system/utils/Slots';
import { useSlotContext } from '@upheal/design-system/utils/useSlotContext';
import { getRadiusStyles } from '@upheal/design-system/utils/radius';

const defaultVariant: DesignSystemButtonVariant = 'solid';
const defaultSize: DesignSystemButtonSize = 'md';
const defaultColorScheme: DesignSystemButtonColorScheme = 'accent1';
const defaultRadius: DesignSystemButtonRadius = 'rounded';

/**
 * Button component wrapping MUI Button, providing styles and variants based on the design system.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (props, ref) => {
        const mergedProps = useSlotContext(props, props.slot);
        const {
            children,
            variant = defaultVariant,
            size = defaultSize,
            colorScheme = defaultColorScheme,
            radius = defaultRadius,
            disabled = false,
            underline = false,
            iconOnly = false,
            sx,
            ...rest
        } = mergedProps;
        const theme = useTheme();
        const colors = theme.palette.ds;

        const childArray = Children.toArray(children);
        const lastChild = childArray[childArray.length - 1];
        const hasTrailingChip =
            isValidElement(lastChild) && lastChild.props.slot === 'chip';

        const { buttonStyles, iconSize, typographyVariant, sizeDef } =
            useMemo(() => {
                const colorStyles = getVariantColorStyles({
                    variant,
                    colorScheme,
                    colors,
                    disabled,
                    underline,
                });
                const sizeStyles = getSizeStyles({ size, variant, iconOnly });
                const radiusStyles = getRadiusStyles({
                    componentRadiusValues: buttonRadiusValues,
                    radius,
                    size,
                    theme,
                });

                const sizeDef = buttonSizeValues[size];
                const iconSize = sizeDef.iconSize;
                const typographyVariant = sizeDef.typographyVariant;

                const buttonStyles: SxProps<Theme> = {
                    minWidth: sizeStyles.minWidth,
                    minHeight: iconOnly ? undefined : 'fit-content',
                    width: sizeStyles.width,
                    height: sizeStyles.height,
                    paddingLeft: sizeStyles.paddingX,
                    paddingRight: hasTrailingChip
                        ? sizeStyles.paddingY // Use vertical padding for consistency
                        : sizeStyles.paddingX,
                    paddingY: sizeStyles.paddingY,
                    borderRadius: radiusStyles.borderRadius,
                    border:
                        variant === 'outline' ||
                        variant === 'buttonGroup' ||
                        variant === 'buttonGroupProminent' ||
                        variant === 'buttonGroupActive'
                            ? `1px solid ${colorStyles.borderColor}`
                            : `1px solid transparent`,
                    backgroundColor: colorStyles.backgroundColor,
                    color: colorStyles.color, // For icons & text children
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    pointerEvents: disabled ? 'none' : 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    verticalAlign: 'middle',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: 'unset',
                    textDecoration: colorStyles.textDecoration,
                    fontWeight: theme.typography.fontWeightSemibold,
                    transition: theme.transitions.create(
                        [
                            'background-color',
                            'border-color',
                            'color',
                            'box-shadow',
                            'outline',
                        ],
                        { duration: theme.transitions.duration.short },
                    ),

                    '&:hover': {
                        ...colorStyles['&:hover'],
                        transform: 'unset', // Root button in theme has the scaling animation
                    },
                    '&:active': {
                        ...colorStyles['&:active'],
                        boxShadow: 'none',
                    },
                    '&:focus-visible': {
                        outlineColor:
                            theme.palette.ds.effects.focusRing.accent1,
                        outlineStyle: 'solid',
                        outlineOffset: '1px',
                        outlineWidth: focusWidthValues.md,
                    },
                    '&.Mui-disabled': {
                        ...colorStyles['&.Mui-disabled'],
                        // need to override MUI's important when setting opacity
                        opacity: '1 !important',
                    },

                    '& .MuiButton-startIcon': {
                        marginLeft: 0,
                        marginRight: sizeDef.gap,
                    },

                    '& .MuiButton-endIcon': {
                        marginRight: 0,
                        marginLeft: sizeDef.gap,
                    },

                    '&.MuiButton-loading': {
                        '& .MuiButton-startIcon': {
                            marginLeft: 0,
                            marginRight: 0,
                        },

                        '& .MuiButton-endIcon': {
                            marginLeft: 0,
                            marginRight: 0,
                        },

                        '&.MuiButton-loadingPositionStart .MuiButton-loadingIndicator':
                            {
                                left: loadingIndicatorHorizontalPositionValues[
                                    size
                                ],
                            },
                        '&.MuiButton-loadingPositionEnd .MuiButton-loadingIndicator':
                            {
                                right: loadingIndicatorHorizontalPositionValues[
                                    size
                                ],
                            },
                    },

                    ...sx,
                };

                return {
                    buttonStyles,
                    iconSize,
                    typographyVariant,
                    colorStyles,
                    sizeDef,
                };
            }, [
                colorScheme,
                colors,
                disabled,
                iconOnly,
                radius,
                size,
                sx,
                theme,
                underline,
                variant,
                hasTrailingChip,
            ]);

        const slots: Slots = useMemo(
            () => ({
                label: {
                    color: 'inherit',
                    component: 'span',
                    variant: typographyVariant,
                    fontWeight: 'inherit',
                    sx: {
                        display: 'inline-block',
                        lineHeight: 'inherit',
                    },
                },
                leadingItem: {
                    color: 'inherit',
                    size: iconSize,
                },
                trailingItem: {
                    color: 'inherit',
                    size: iconSize,
                },
                icon: {
                    color: 'inherit',
                    size: iconSize,
                },
            }),
            [typographyVariant, iconSize],
        );

        return (
            <SlotProvider value={slots}>
                <MuiButton
                    ref={ref}
                    variant="ds" // unsets most styles added by SUI on theme level
                    disableRipple // Disable MUI ripple effect
                    disableFocusRipple // Disable MUI focus ripple
                    disabled={disabled}
                    sx={buttonStyles}
                    component={variant === 'link' ? 'a' : 'button'}
                    {...rest}
                >
                    {typeof children === 'string' ? (
                        <Typography slot="label">{children}</Typography>
                    ) : iconOnly && rest.loading ? null : (
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={sizeDef.gap}
                            sx={{
                                // make sure content doesn't overflow the button
                                minWidth: 0,
                            }}
                        >
                            {children}
                        </Stack>
                    )}
                </MuiButton>
            </SlotProvider>
        );
    },
);
